import { motion } from "framer-motion";
import {
  ShieldCheck,
  BriefcaseBusiness,
  FileCheck,
  Clock3,
  BadgeCheck,
  Headphones,
  ArrowUpRight,
} from "lucide-react";

const reasons = [
  {
    number: "01",
    icon: ShieldCheck,
    title: "Experienced Legal Professionals",
    description:
      "Our experienced legal consultants guide businesses through registrations, taxation, licensing, intellectual property, and statutory compliance with complete accuracy.",
  },
  {
    number: "02",
    icon: BriefcaseBusiness,
    title: "Comprehensive Business Solutions",
    description:
      "From business incorporation to regulatory approvals and annual compliance, we provide every legal service your business needs under one roof.",
  },
  {
    number: "03",
    icon: FileCheck,
    title: "Accurate Documentation",
    description:
      "We prepare and verify every application and legal document carefully, minimizing rejection risks and ensuring smooth government approvals.",
  },
  {
    number: "04",
    icon: Clock3,
    title: "Fast & Transparent Process",
    description:
      "Our streamlined workflow, regular status updates, and dedicated support ensure your registration and compliance work is completed on time.",
  },
  {
    number: "05",
    icon: BadgeCheck,
    title: "Affordable & Reliable",
    description:
      "Transparent pricing, no hidden charges, and dependable legal assistance make us a trusted partner for startups, MSMEs, and enterprises.",
  },
  {
    number: "06",
    icon: Headphones,
    title: "Dedicated Client Support",
    description:
      "Our support team remains available throughout your legal journey, helping you with documentation, compliance, and post-registration assistance.",
  },
];

const WhyChooseUs = () => {
  return (
    <section
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        py-6
        sm:py-8
        md:py-10
        lg:py-14
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          top-24
          h-56
          w-56
          rounded-full
          bg-[var(--brand-primary)]/[0.05]
          blur-[100px]
          sm:h-64
          sm:w-64
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-32
          bottom-20
          h-56
          w-56
          rounded-full
          bg-[var(--brand-primary)]/[0.04]
          blur-[100px]
          sm:h-64
          sm:w-64
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          lg:px-8
        "
      >
        {/* =================================================
            SECTION HEADER
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
          className="
            mx-auto
            mb-9
            max-w-2xl
            text-center
            sm:mb-10
            lg:mb-12
          "
        >
          {/* label */}
          <span
            className="
              inline-flex
              items-center
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/[0.08]
              px-3
              py-1.5
              text-[10px]
              font-semibold
              uppercase
              tracking-[2px]
              text-orange-400
              sm:px-4
              sm:py-2
              sm:text-xs
              sm:tracking-[2.5px]
            "
          >
            Why Choose US
          </span>

          {/* Heading */}

          <h2
            className="
              mt-4
              text-2xl
              font-bold
              leading-tight
              tracking-tight
              text-[var(--text-primary)]
              sm:mt-5
              sm:text-3xl
              md:text-4xl
              lg:text-5xl
            "
          >
            Your Trusted{" "}
            <span className="text-[var(--brand-primary)]">Legal Partner</span>
          </h2>

          {/* Description */}

          <p
            className="
              mx-auto
              mt-3
              max-w-xl
              text-xs
              leading-5
              text-[var(--text-muted)]
              sm:mt-4
              sm:text-sm
              sm:leading-6
              md:text-base
              md:leading-7
            "
          >
            We combine legal expertise, industry knowledge, and customer-focused
            service to help businesses stay compliant and grow with confidence.
          </p>
        </motion.div>

        {/* =================================================
            FEATURES
        ================================================== */}

        <div
          className="
            grid
            grid-cols-1
            gap-4
            md:grid-cols-2
            md:gap-5
            md:gap-x-8
          "
        >
          {reasons.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.title}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  amount: 0.1,
                }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.06,
                  ease: "easeOut",
                }}
                className="
                  group
                  relative
                  overflow-hidden
                  rounded-2xl
                  border
                  border-[var(--border-primary)]
                  bg-[var(--card-bg-secondary)]
                  py-5
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  hover:border-[var(--brand-primary)]/[0.30]
                  sm:py-6
                  lg:py-7
                "
              >
                {/* Hover background */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    -inset-x-3
                    inset-y-1
                    rounded-xl
                    bg-[var(--brand-primary)]/[0.025]
                    opacity-0
                    blur-lg
                    transition-opacity
                    duration-300
                    group-hover:opacity-100
                  "
                />

                {/* =================================================
                    CONTENT ROW
                ================================================== */}

                <div
                  className="
                    relative
                    flex
                    items-start
                    gap-3
                    px-4
                    sm:gap-4
                    sm:px-5
                    lg:px-6
                  "
                >
                  {/* Number */}

                  <span
                    className="
                      w-7
                      shrink-0
                      pt-1
                      text-[10px]
                      font-semibold
                      tracking-[1.5px]
                      text-[var(--brand-primary)]/[0.70]
                      sm:w-9
                      sm:text-xs
                    "
                  >
                    {item.number}
                  </span>

                  {/* Icon */}

                  <div
                    className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-lg
                      border
                      border-[var(--border-primary)]
                      bg-[var(--card-bg)]
                      text-[var(--brand-primary)]
                      transition-all
                      duration-300
                      group-hover:border-[var(--brand-primary)]/[0.30]
                      group-hover:bg-[var(--brand-primary)]
                      group-hover:text-white
                      sm:h-11
                      sm:w-11
                      sm:rounded-xl
                    "
                  >
                    <Icon
                      size={19}
                      strokeWidth={1.8}
                      className="
                        transition-transform
                        duration-300
                        group-hover:scale-110
                      "
                    />
                  </div>

                  {/* Text Content */}

                  <div className="min-w-0 flex-1">
                    <div
                      className="
                        flex
                        items-start
                        justify-between
                        gap-2
                      "
                    >
                      <h3
                        className="
                          text-base
                          font-semibold
                          leading-snug
                          text-[var(--text-primary)]
                          transition-colors
                          duration-300
                          group-hover:text-[var(--brand-light)]
                          sm:text-lg
                        "
                      >
                        {item.title}
                      </h3>

                      <ArrowUpRight
                        size={16}
                        className="
                          mt-0.5
                          shrink-0
                          text-[var(--text-muted)]
                          transition-all
                          duration-300
                          group-hover:-translate-y-0.5
                          group-hover:translate-x-0.5
                          group-hover:text-[var(--brand-primary)]
                          sm:h-[18px]
                          sm:w-[18px]
                        "
                      />
                    </div>

                    <p
                      className="
                        mt-2
                        max-w-xl
                        text-xs
                        leading-5
                        text-[var(--text-muted)]
                        transition-colors
                        duration-300
                        group-hover:text-[var(--text-secondary)]
                        sm:mt-2.5
                        sm:text-sm
                        sm:leading-6
                      "
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* =================================================
            BOTTOM TRUST LINE
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
          }}
          whileInView={{
            opacity: 1,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.5,
            delay: 0.2,
          }}
          className="
            mt-7
            flex
            flex-wrap
            items-center
            justify-center
            gap-2
            text-center
            sm:mt-9
            sm:gap-3
          "
        >
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                className="
                  text-xs
                  text-[var(--brand-primary)]
                  sm:text-sm
                "
              >
                ★
              </span>
            ))}
          </div>

          <span
            className="
              text-xs
              text-[var(--text-muted)]
              sm:text-sm
            "
          >
            Professional support you can rely on
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
